import { useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowDownToLine, ArrowUpFromLine, History, Home, Megaphone, RefreshCw, User } from 'lucide-react';
import { api } from '@/lib/api';
import { getWebApp, openTelegramLink } from '@/lib/telegram';
import { useI18n, LANGS, type Lang } from '@/i18n';
import { useAuth } from '@/context/AuthContext';
import { BrandMark, Spinner } from './ui';

/** Language switch in the header (pt ⇄ en), saved for the bot's messages too. */
function LangSwitch() {
    const { lang, setLang } = useI18n();
    const next: Lang = LANGS[(LANGS.indexOf(lang) + 1) % LANGS.length];
    return (
        <button
            type="button"
            onClick={() => { setLang(next); void api.post('/lang', { lang: next }).catch(() => undefined); }}
            className="px-2.5 py-1 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-white/5 border border-white/10 active:scale-95"
        >
            {lang === 'pt' ? '🇧🇷 PT' : '🇬🇧 EN'}
        </button>
    );
}

/** Telegram's back button on every page but the home screen. */
function useTelegramBack() {
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        const tg = getWebApp();
        if (!tg || !tg.initData) return;
        const back = () => navigate(-1);
        if (location.pathname === '/') { tg.BackButton.hide(); return; }
        tg.BackButton.show();
        tg.BackButton.onClick(back);
        return () => tg.BackButton.offClick(back);
    }, [location.pathname, navigate]);
}

/** The official channel must be joined first, as in the bot (no channel set = no check). */
function SubscriptionGate({ children }: { children: React.ReactNode }) {
    const { t } = useI18n();
    const { me } = useAuth();
    const q = useQuery({
        queryKey: ['subscription'],
        queryFn: async () => (await api.get<{ required: boolean; subscribed: boolean; error?: boolean; url?: string | null }>('/subscription')).data,
        enabled: !!me?.channel && !me?.isAdmin,
        staleTime: 60_000
    });
    if (!me?.channel || me.isAdmin) return <>{children}</>;
    if (q.isLoading) return <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-brand-300" /></div>;
    if (q.data && (!q.data.required || q.data.subscribed)) return <>{children}</>;
    const url = (q.data && q.data.url) || me.channel.url;
    return (
        <div className="card-lime text-center space-y-4 py-8 animate-fade-in-up">
            <Megaphone className="w-10 h-10 mx-auto text-brand-300" />
            <div className="space-y-1.5">
                <h2 className="text-lg font-bold">{t('gate.channel.title')}</h2>
                <p className="text-sm text-ink-muted">{t('gate.channel.text')}</p>
            </div>
            {url && <button className="btn-primary w-full" onClick={() => openTelegramLink(url)}>{t('gate.channel.join')}</button>}
            <button className="btn-secondary w-full" disabled={q.isFetching} onClick={() => q.refetch()}>
                {q.isFetching ? <Spinner className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />} {t('gate.channel.check')}
            </button>
            {q.data && q.data.error && <p className="text-xs text-ink-muted">{t('gate.channel.error')}</p>}
        </div>
    );
}

function BottomNav() {
    const { t } = useI18n();
    const items = [
        { to: '/', label: t('nav.home'), icon: Home, end: true },
        { to: '/deposit', label: t('nav.deposit'), icon: ArrowDownToLine },
        { to: '/withdraw', label: t('nav.withdraw'), icon: ArrowUpFromLine },
        { to: '/history', label: t('nav.history'), icon: History },
        { to: '/profile', label: t('nav.account'), icon: User }
    ];
    return (
        <nav className="fixed bottom-0 inset-x-0 z-40 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl border-t border-white/[.06]"
             style={{ background: 'linear-gradient(180deg, rgba(14,21,15,.8) 0%, rgba(6,10,6,.95) 100%)' }}>
            <div className="h-px bg-gradient-to-r from-transparent via-brand-400/50 to-transparent" />
            <ul className="max-w-lg mx-auto grid grid-cols-5 px-1">
                {items.map(item => (
                    <li key={item.to}>
                        <NavLink to={item.to} end={item.end}
                            className={({ isActive }) => clsx('group relative flex flex-col items-center gap-1 py-2.5 text-[11px] transition-colors',
                                isActive ? 'text-brand-300' : 'text-ink-muted hover:text-ink')}>
                            {({ isActive }) => (
                                <>
                                    <span aria-hidden className={clsx('absolute top-1 w-12 h-12 rounded-2xl transition-all duration-300',
                                        isActive ? 'opacity-100 scale-100 bg-brand-400/10 border border-brand-400/30 shadow-[0_0_18px_-4px_rgba(158,240,31,.5)]'
                                            : 'opacity-0 scale-75 border border-transparent')} />
                                    <item.icon className={clsx('relative z-10 w-5 h-5 transition-transform', isActive ? 'scale-110' : 'group-active:scale-90')} />
                                    <span className={clsx('relative z-10', isActive && 'font-semibold')}>{item.label}</span>
                                </>
                            )}
                        </NavLink>
                    </li>
                ))}
            </ul>
        </nav>
    );
}

export function Layout() {
    const location = useLocation();
    useTelegramBack();
    return (
        <div className="min-h-screen pb-24">
            <header className="sticky top-0 z-30 backdrop-blur-xl bg-surface/75 border-b border-white/[.06]">
                <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-2">
                    <Link to="/" className="shrink-0 active:scale-95 transition"><BrandMark size="md" /></Link>
                    <div className="flex-1" />
                    <LangSwitch />
                </div>
                <div className="h-px bg-gradient-to-r from-transparent via-brand-400/60 to-transparent" />
            </header>
            <main key={location.pathname} className="max-w-lg mx-auto px-4 py-4 animate-fade-in-up">
                <SubscriptionGate><Outlet /></SubscriptionGate>
            </main>
            <BottomNav />
        </div>
    );
}
